import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  ModelScopeFactory,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import {
  ResourcePageShell,
  type BreadcrumbItem,
} from '../resources/resource-page-shell';
import { workflowRunsBreadcrumbs } from './workflow-breadcrumbs';
import { WorkflowRunsList } from './workflow-runs-list';
import { WorkflowRunsModel, useWorkflowRunsModel } from './workflow-runs.model';
import { WorkflowRunActionTargetsModel } from './workflow-run-action-targets.model';

const WorkflowRunsContent: React.FC<{ workflowId: string }> = ({
  workflowId,
}) => {
  const org = useWhoami((s) => s.org);
  const model = useWorkflowRunsModel();
  const workflowName = useWatchableValue(model.workflowNameWatchable);

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () =>
      workflowRunsBreadcrumbs({
        orgSlug: org.slug,
        workflowId,
        workflowName,
      }),
    [org.slug, workflowId, workflowName]
  );

  return (
    <ResourcePageShell breadcrumbs={breadcrumbs}>
      {/* Matches the Automations list and the Library: a 1640px cap, and 48px
          gutters at `lg` of which `ResourceListView` contributes 32px itself. */}
      <div className="mx-auto max-w-[1640px] lg:px-4">
        <WorkflowRunsList workflowId={workflowId} />
      </div>
    </ResourcePageShell>
  );
};

export const WorkflowRunsPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const createModels = useCallback(
    () => [
      new WorkflowRunsModel({
        workflowId: workflowId ?? '',
        orgId,
        client,
        queryClient,
      }),
      // Carries the rows the cancel/retry actions act on; the list keeps it in
      // step with its selection.
      new WorkflowRunActionTargetsModel({ workflowId: workflowId ?? '' }),
    ],
    [workflowId, orgId, client, queryClient]
  );
  if (workflowId === undefined || workflowId === '') {
    return null;
  }
  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[workflowId, orgId]}
      key={workflowId}
    >
      <WorkflowRunsContent workflowId={workflowId} />
    </ModelScopeFactory>
  );
};
